<template>
  <b-modal v-model="localVisible" :title="`User permissions for ${subject}`" @ok="onSave" @hide="onHide" size="xl">
    <template #title>
      User permissions for {{ subject }}
      <modal-help topic="user-permissions" />
    </template>
    <p class="upr-hint">
      These rules run for the logged-in user. The first matching rule shows or hides {{ subject }}.
      If none match, {{ subject }} stays visible to anyone who is logged in.
      A condition can use a column on that user, a field on a record the user points to, or a field on a record that points at the user.
    </p>
    <p v-if="!userSpreadSheet" class="upr-hint upr-warn">
      Add a login form that uses a spreadsheet before these rules can read a user.
    </p>

    <div v-if="localRules.length" class="upr-rule-list">
      <template v-for="(rule, idx) in localRules" :key="rule.id">
        <div v-if="idx > 0" class="upr-connector"><span></span></div>
        <div class="upr-rule-item" :class="{ 'upr-rule-editing': editingIndex === idx }">
          <button type="button" class="upr-rule-open" @click="editRule(idx)">
            <span class="upr-type-badge" :class="'upr-type-' + rule.type">{{ rule.type.toUpperCase() }}</span>
            <span class="upr-rule-body">
              <template v-if="rule.type !== 'else'">
                <span class="upr-conditions">
                  <template v-for="(cond, ci) in rule.conditions" :key="ci">
                    <span v-if="ci > 0" class="upr-join">{{ rule.conditionMatchMode === 'any' ? 'OR' : 'AND' }}</span>
                    <span class="upr-cond">{{ conditionSummary(cond) }}</span>
                  </template>
                  <span v-if="!rule.conditions || !rule.conditions.length" class="upr-empty">(no conditions)</span>
                </span>
              </template>
              <span class="upr-arrow">&rarr;</span>
              <span class="upr-action">{{ rule.action === 'hide' ? 'Hide' : 'Show' }}</span>
            </span>
          </button>
          <button type="button" class="upr-remove" @click.stop="removeRule(idx)" aria-label="Remove rule">&times;</button>
        </div>
      </template>
    </div>

    <div v-if="!localRules.length && !isEditing" class="upr-empty-state">
      No user permission rules. {{ subject.charAt(0).toUpperCase() + subject.slice(1) }} is visible to every logged-in user.
    </div>

    <div v-if="isEditing" class="upr-edit">
      <div class="upr-edit-title">{{ editingIndex !== null ? 'Edit' : 'New' }} rule</div>

      <div v-if="availableTypes.length > 1" class="upr-row">
        <label>Rule type</label>
        <select v-model="editType" class="upr-select">
          <option v-for="opt in availableTypes" :key="opt" :value="opt">{{ opt.toUpperCase() }}</option>
        </select>
      </div>

      <template v-if="editType !== 'else'">
        <div class="upr-section-head">
          <span>Conditions</span>
          <div v-if="editConditions.length > 1" class="upr-match">
            <button type="button" :class="{ active: editMatchMode === 'all' }" @click="editMatchMode = 'all'">ALL</button>
            <button type="button" :class="{ active: editMatchMode === 'any' }" @click="editMatchMode = 'any'">ANY</button>
          </div>
        </div>

        <div v-for="(cond, ci) in editConditions" :key="ci" class="upr-condition">
          <select v-model="cond.source" class="upr-select" @change="onSourceChange(cond)">
            <option value="field">User field</option>
            <option value="related">Record the user points to</option>
            <option value="reverse">Record that points at the user</option>
          </select>

          <select v-if="cond.source === 'field'" v-model="cond.field" class="upr-select" @change="onUserFieldChange(cond)">
            <option value="" disabled>Field...</option>
            <option v-for="col in userColumns" :key="col.uniqueTitle" :value="col.uniqueTitle">{{ col.title || col.uniqueTitle }}</option>
          </select>

          <template v-if="cond.source === 'related'">
            <select v-model="cond.relationshipColumn" class="upr-select" @change="onForwardChange(cond)">
              <option value="" disabled>Relationship...</option>
              <option v-for="col in forwardColumns" :key="col.uniqueTitle" :value="col.uniqueTitle">{{ col.title || col.uniqueTitle }}</option>
            </select>
            <select v-if="cond.relationshipColumn" v-model="cond.relatedQuantifier" class="upr-select upr-quant">
              <option value="any">any</option>
              <option value="all">all</option>
              <option value="none">none of</option>
            </select>
            <select v-if="cond.relationshipColumn" v-model="cond.field" class="upr-select" @change="onRelatedFieldChange(cond)">
              <option value="" disabled>Field...</option>
              <option v-for="col in columnsOn(cond.relatedSpreadsheet)" :key="col.uniqueTitle" :value="col.uniqueTitle">{{ col.title || col.uniqueTitle }}</option>
            </select>
          </template>

          <template v-if="cond.source === 'reverse'">
            <select v-model="cond.reverseKey" class="upr-select" @change="onReverseChange(cond)">
              <option value="" disabled>Relationship...</option>
              <option v-for="col in reverseColumns" :key="col.key" :value="col.key">{{ col.label }}</option>
            </select>
            <select v-if="cond.sourceRelationshipColumn" v-model="cond.relatedQuantifier" class="upr-select upr-quant">
              <option value="any">any</option>
              <option value="all">all</option>
              <option value="none">none of</option>
            </select>
            <select v-if="cond.sourceRelationshipColumn" v-model="cond.field" class="upr-select" @change="onRelatedFieldChange(cond)">
              <option value="" disabled>Field...</option>
              <option v-for="col in columnsOn(cond.sourceSpreadsheet)" :key="col.uniqueTitle" :value="col.uniqueTitle">{{ col.title || col.uniqueTitle }}</option>
            </select>
          </template>

          <select v-model="cond.operator" class="upr-select">
            <option v-for="op in operatorsFor(cond.dataType)" :key="op.key" :value="op.key">{{ op.label }}</option>
          </select>

          <template v-if="!isBlank(cond.operator)">
            <select v-if="cond.dataType === 'checkbox'" v-model="cond.value" class="upr-select">
              <option value="true">Checked</option>
              <option value="false">Unchecked</option>
            </select>
            <div v-else-if="cond.dataType === 'relationship'" class="upr-rel">
              <span v-for="rec in cond.relSelectedRecords || []" :key="rec.id" class="upr-tag">
                {{ rec.displayValue }}
                <button type="button" @click="removeRecord(cond, rec.id)" aria-label="Remove record">&times;</button>
              </span>
              <input
                v-model="cond.relSearchQuery"
                class="upr-input"
                type="text"
                placeholder="Search records..."
                @input="onSearchInput(cond)"
              />
              <button
                v-for="rec in cond.relSearchResults || []"
                :key="rec.id"
                type="button"
                class="upr-hit"
                @click="selectRecord(cond, rec)"
              >{{ rec.displayValue }}</button>
            </div>
            <input v-else v-model="cond.value" class="upr-input" type="text" />
          </template>

          <button type="button" class="upr-remove" @click="editConditions.splice(ci, 1)" aria-label="Remove condition">&times;</button>
        </div>

        <button type="button" class="upr-add" @click="editConditions.push(newCondition())">+ condition</button>
      </template>

      <div class="upr-row">
        <label>Then</label>
        <select v-model="editAction" class="upr-select">
          <option value="show">Show {{ subject }}</option>
          <option value="hide">Hide {{ subject }}</option>
        </select>
      </div>

      <div class="upr-edit-actions">
        <button type="button" class="upr-save" @click="saveRule">Save rule</button>
        <button type="button" class="upr-cancel" @click="cancelEdit">Cancel</button>
      </div>
    </div>

    <button v-if="!isEditing" type="button" class="upr-add upr-add-rule" @click="startAddRule">+ add rule</button>
  </b-modal>
</template>

<script>
import { ruleChainTypes } from '../../lib/rule-chain-types.js'
import { operatorsForDataType, isBlankOperator } from '../../../../shared/display-rules.js'
import { findTopLevelLoginSpreadSheet } from '../../../../shared/logged-in-user-default.js'

const SKIP_TYPES = new Set(['password', 'image', 'file'])
const TYPE_MAP = {
  text: 'string',
  number: 'number',
  dateTime: 'datetime',
  formula: 'string',
  richText: 'string',
  relationship: 'relationship',
  checkbox: 'checkbox',
  multiselect: 'string',
  address: 'string',
  duration: 'number',
  sequence: 'number',
  signature: 'signature'
}

function generateRuleId () {

  return `upr_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
}

function columnDataType (column) {

  return TYPE_MAP[column && column.type] || 'string'
}

export default {
  name: 'UserPermissionRuleEditor',
  props: {
    visible: { type: Boolean, default: false },
    rules: { type: Object, default: null },
    spreadsheets: { type: Array, default: () => [] },
    pages: { type: Array, default: () => [] },
    subject: { type: String, default: 'this page' },
    applicationUniqueTitle: { type: String, default: '' }
  },
  emits: ['save', 'update:visible'],
  data () {
    return {
      localVisible: false,
      localRules: [],
      isEditing: false,
      editingIndex: null,
      editType: 'if',
      editConditions: [],
      editMatchMode: 'all',
      editAction: 'show'
    }
  },
  computed: {
    availableTypes () {
      return ruleChainTypes(this.localRules, this.editingIndex)
    },
    userSpreadSheet () {
      return findTopLevelLoginSpreadSheet(this.pages)
    },
    userSheet () {
      return (this.spreadsheets || []).find(sheet => sheet && sheet.uniqueTitle === this.userSpreadSheet) || null
    },
    userColumns () {
      return ((this.userSheet && this.userSheet.columns) || []).filter(column => column && !SKIP_TYPES.has(column.type))
    },
    forwardColumns () {
      return this.userColumns.filter(column => column.type === 'relationship' && column.relatedSpreadSheet)
    },
    reverseColumns () {
      const userTitle = this.userSpreadSheet
      const options = []

      if (!userTitle) return options

      for (const sheet of this.spreadsheets || []) {

        if (!sheet || sheet.uniqueTitle === userTitle) continue

        for (const column of sheet.columns || []) {

          if (!column || column.type !== 'relationship' || column.relatedSpreadSheet !== userTitle) continue

          options.push({
            key: `${sheet.uniqueTitle}::${column.uniqueTitle}`,
            sourceSpreadsheet: sheet.uniqueTitle,
            sourceRelationshipColumn: column.uniqueTitle,
            label: `${sheet.title || sheet.uniqueTitle} · ${column.title || column.uniqueTitle}`
          })
        }
      }

      return options
    }
  },
  watch: {
    visible (val) {
      this.localVisible = val
      if (val) {
        const stored = this.rules && Array.isArray(this.rules.rules) ? this.rules.rules : []
        this.localRules = JSON.parse(JSON.stringify(stored))
        this.cancelEdit()
      }
    },
    localVisible (val) {
      this.$emit('update:visible', val)
    }
  },
  methods: {
    operatorsFor (dataType) {
      return operatorsForDataType(dataType || 'string')
    },
    isBlank (operator) {
      return isBlankOperator(operator)
    },
    columnsOn (uniqueTitle) {
      const sheet = (this.spreadsheets || []).find(item => item && item.uniqueTitle === uniqueTitle)
      return ((sheet && sheet.columns) || []).filter(column => column && !SKIP_TYPES.has(column.type))
    },
    sheetTitle (uniqueTitle) {
      const sheet = (this.spreadsheets || []).find(item => item && item.uniqueTitle === uniqueTitle)
      return (sheet && (sheet.title || sheet.uniqueTitle)) || uniqueTitle || '?'
    },
    columnTitle (columns, uniqueTitle) {
      const column = (columns || []).find(item => item && item.uniqueTitle === uniqueTitle)
      return (column && (column.title || column.uniqueTitle)) || uniqueTitle || '?'
    },
    newCondition () {
      return {
        source: 'field',
        field: '',
        operator: '=',
        value: '',
        dataType: 'string',
        relationshipColumn: '',
        relatedSpreadsheet: '',
        sourceSpreadsheet: '',
        sourceRelationshipColumn: '',
        reverseKey: '',
        relatedQuantifier: 'any',
        relSelectedRecords: [],
        relSearchQuery: '',
        relSearchResults: []
      }
    },
    onSourceChange (cond) {
      cond.field = ''
      cond.relationshipColumn = ''
      cond.relatedSpreadsheet = ''
      cond.sourceSpreadsheet = ''
      cond.sourceRelationshipColumn = ''
      cond.reverseKey = ''
      cond.relatedQuantifier = 'any'
      cond.value = ''
      cond.relSelectedRecords = []
      cond.dataType = 'string'
      cond.operator = '='
    },
    setFieldType (cond, column) {
      cond.dataType = columnDataType(column)
      const operators = this.operatorsFor(cond.dataType)
      if (!operators.some(op => op.key === cond.operator)) cond.operator = operators[0] ? operators[0].key : '='
      cond.value = cond.dataType === 'checkbox' ? 'true' : ''
      cond.relSelectedRecords = []
    },
    onUserFieldChange (cond) {
      this.setFieldType(cond, this.userColumns.find(column => column.uniqueTitle === cond.field))
    },
    onForwardChange (cond) {
      const column = this.forwardColumns.find(item => item.uniqueTitle === cond.relationshipColumn)
      cond.relatedSpreadsheet = column ? column.relatedSpreadSheet : ''
      cond.field = ''
      cond.dataType = 'string'
    },
    onReverseChange (cond) {
      const column = this.reverseColumns.find(item => item.key === cond.reverseKey)
      cond.sourceSpreadsheet = column ? column.sourceSpreadsheet : ''
      cond.sourceRelationshipColumn = column ? column.sourceRelationshipColumn : ''
      cond.field = ''
      cond.dataType = 'string'
    },
    onRelatedFieldChange (cond) {
      const sheet = cond.source === 'reverse' ? cond.sourceSpreadsheet : cond.relatedSpreadsheet
      this.setFieldType(cond, this.columnsOn(sheet).find(column => column.uniqueTitle === cond.field))
    },
    relatedSheetFor (cond) {
      if (cond.dataType !== 'relationship') return ''
      if (cond.source === 'field') {
        const column = this.userColumns.find(item => item.uniqueTitle === cond.field)
        return column ? column.relatedSpreadSheet : ''
      }
      const sheet = cond.source === 'reverse' ? cond.sourceSpreadsheet : cond.relatedSpreadsheet
      const column = this.columnsOn(sheet).find(item => item.uniqueTitle === cond.field)
      return column ? column.relatedSpreadSheet : ''
    },
    onSearchInput (cond) {
      if (cond._searchTimer) clearTimeout(cond._searchTimer)
      cond._searchTimer = setTimeout(() => this.searchRecords(cond), 250)
    },
    async searchRecords (cond) {
      const sheet = this.relatedSheetFor(cond)
      if (!sheet) return
      try {
        const headers = {}
        if (this.applicationUniqueTitle) headers['application-unique-title'] = this.applicationUniqueTitle
        const response = await fetch(
          `${VUE_APP_PROXY_SERVER_URL}/data/${sheet}/search?q=${encodeURIComponent(cond.relSearchQuery || '')}`,
          { headers }
        )
        const data = await response.json()
        const selected = new Set((cond.relSelectedRecords || []).map(rec => rec.id))
        cond.relSearchResults = (Array.isArray(data) ? data : []).filter(rec => !selected.has(rec.id))
      } catch (err) {
        cond.relSearchResults = []
      }
    },
    selectRecord (cond, rec) {
      if (!cond.relSelectedRecords) cond.relSelectedRecords = []
      if (!cond.relSelectedRecords.find(item => item.id === rec.id)) {
        cond.relSelectedRecords.push({ id: rec.id, displayValue: rec.displayValue })
      }
      cond.relSearchResults = (cond.relSearchResults || []).filter(item => item.id !== rec.id)
      cond.relSearchQuery = ''
      cond.value = cond.relSelectedRecords.map(item => item.id)
    },
    removeRecord (cond, id) {
      cond.relSelectedRecords = (cond.relSelectedRecords || []).filter(item => item.id !== id)
      cond.value = cond.relSelectedRecords.map(item => item.id)
    },
    conditionSummary (cond) {
      if (!cond) return '?'
      let label = cond.field || '?'
      if (cond.source === 'field') label = this.columnTitle(this.userColumns, cond.field)
      if (cond.source === 'related') {
        const quantifier = cond.relatedQuantifier || 'any'
        label = `${quantifier} ${this.columnTitle(this.forwardColumns, cond.relationshipColumn)}.${this.columnTitle(this.columnsOn(cond.relatedSpreadsheet), cond.field)}`
      }
      if (cond.source === 'reverse') {
        const quantifier = cond.relatedQuantifier || 'any'
        label = `${quantifier} ${this.sheetTitle(cond.sourceSpreadsheet)}.${this.columnTitle(this.columnsOn(cond.sourceSpreadsheet), cond.field)}`
      }
      const operator = this.operatorsFor(cond.dataType).find(op => op.key === cond.operator)
      const opLabel = operator ? operator.label : (cond.operator || '=')
      if (this.isBlank(cond.operator)) return `${label} ${opLabel}`
      let value = cond.value == null ? '' : cond.value
      if (cond.dataType === 'relationship') value = (cond.relSelectedRecords || []).map(rec => rec.displayValue).join(', ') || '(none)'
      if (cond.dataType === 'checkbox') value = cond.value === 'false' ? 'Unchecked' : 'Checked'
      return `${label} ${opLabel} ${value}`
    },
    startAddRule () {
      this.isEditing = true
      this.editingIndex = null
      this.editType = this.availableTypes[0] || 'if'
      this.editConditions = [this.newCondition()]
      this.editMatchMode = 'all'
      this.editAction = 'show'
    },
    editRule (idx) {
      const rule = this.localRules[idx]
      this.isEditing = true
      this.editingIndex = idx
      this.editType = rule.type
      this.editAction = rule.action === 'hide' ? 'hide' : 'show'
      this.editMatchMode = rule.conditionMatchMode || 'all'
      this.editConditions = JSON.parse(JSON.stringify(rule.conditions || [])).map(cond => ({
        ...this.newCondition(),
        ...cond,
        reverseKey: cond.source === 'reverse' && cond.sourceSpreadsheet && cond.sourceRelationshipColumn
          ? `${cond.sourceSpreadsheet}::${cond.sourceRelationshipColumn}`
          : (cond.reverseKey || ''),
        relSearchQuery: '',
        relSearchResults: [],
        relSelectedRecords: cond.relSelectedRecords || []
      }))
    },
    saveRule () {
      const conditions = (this.editType === 'else' ? [] : this.editConditions).map(cond => {
        const clean = { ...cond }
        delete clean.relSearchQuery
        delete clean.relSearchResults
        delete clean._searchTimer
        delete clean.reverseKey
        return clean
      })
      const rule = {
        id: this.editingIndex !== null ? this.localRules[this.editingIndex].id : generateRuleId(),
        type: this.editType,
        conditions,
        conditionMatchMode: this.editMatchMode,
        action: this.editAction === 'hide' ? 'hide' : 'show'
      }
      if (this.editingIndex !== null) {
        this.localRules.splice(this.editingIndex, 1, rule)
      } else {
        const last = this.localRules[this.localRules.length - 1]
        if (last && last.type === 'else' && rule.type !== 'else') this.localRules.splice(this.localRules.length - 1, 0, rule)
        else this.localRules.push(rule)
      }
      this.cancelEdit()
    },
    removeRule (idx) {
      this.localRules.splice(idx, 1)
      if (this.editingIndex === idx) this.cancelEdit()
      else if (this.editingIndex !== null && this.editingIndex > idx) this.editingIndex -= 1
    },
    cancelEdit () {
      this.isEditing = false
      this.editingIndex = null
      this.editConditions = []
      this.editAction = 'show'
    },
    onSave () {
      if (this.isEditing) this.saveRule()
      this.$emit('save', { rules: this.localRules })
    },
    onHide () {
      this.cancelEdit()
    }
  }
}
</script>

<style scoped>
.upr-hint {
  color: #b0b0ae;
  font-size: 0.85rem;
  margin-bottom: 12px;
}
.upr-warn {
  color: #f0ad4e;
}
.upr-rule-list {
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-bottom: 12px;
}
.upr-connector {
  height: 14px;
  display: flex;
  justify-content: center;
}
.upr-connector span {
  width: 1px;
  background: #3a3a37;
}
.upr-rule-item {
  display: flex;
  align-items: stretch;
  gap: 6px;
  border: 1px solid #3a3a37;
  border-radius: 6px;
  background: #1a1a17;
}
.upr-rule-item.upr-rule-editing {
  border-color: #9ad12b;
}
.upr-rule-open {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  text-align: left;
  background: transparent;
  border: 0;
  color: inherit;
  padding: 8px 10px;
  cursor: pointer;
}
.upr-type-badge {
  min-width: 54px;
  text-align: center;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  border-radius: 4px;
  padding: 3px 6px;
}
.upr-type-if { background: #1e2e12; color: #9ad12b; }
.upr-type-elseif { background: #2e2512; color: #d0a05b; }
.upr-type-else { background: #251e30; color: #a05bd0; }
.upr-rule-body {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.upr-conditions {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
}
.upr-cond, .upr-action {
  color: #e8e8e6;
  font-size: 0.78rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}
.upr-action {
  color: #9ad12b;
}
.upr-join, .upr-arrow, .upr-empty, .upr-empty-state {
  color: #8a8a88;
  font-size: 0.75rem;
}
.upr-empty-state {
  margin-bottom: 12px;
}
.upr-remove {
  border: 0;
  background: transparent;
  color: #8a8a88;
  cursor: pointer;
  padding: 0 8px;
}
.upr-edit {
  border: 1px solid #3a3a37;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 12px;
}
.upr-edit-title {
  font-weight: 600;
  margin-bottom: 10px;
  color: #e8e8e6;
}
.upr-row, .upr-section-head, .upr-edit-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.upr-row label, .upr-section-head span {
  min-width: 72px;
  color: #b0b0ae;
  font-size: 0.8rem;
}
.upr-condition {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  margin-bottom: 8px;
}
.upr-select, .upr-input {
  background: #121210;
  color: #e8e8e6;
  border: 1px solid #3a3a37;
  border-radius: 4px;
  padding: 4px 6px;
  font-size: 0.8rem;
}
.upr-quant {
  max-width: 90px;
}
.upr-match button, .upr-add, .upr-save, .upr-cancel {
  background: transparent;
  border: 1px solid #3a3a37;
  color: #b0b0ae;
  border-radius: 4px;
  padding: 3px 8px;
  cursor: pointer;
  font-size: 0.75rem;
}
.upr-match button.active, .upr-save {
  color: #9ad12b;
  border-color: #9ad12b;
}
.upr-add-rule {
  margin-top: 4px;
}
.upr-rel {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
}
.upr-tag, .upr-hit {
  background: #2a2a27;
  color: #e8e8e6;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 0.75rem;
}
.upr-hit {
  border: 0;
  cursor: pointer;
}
.upr-tag button {
  border: 0;
  background: transparent;
  color: #b0b0ae;
  cursor: pointer;
}
</style>
